import type {
  StoreDomainDto,
  CreateStoreDomainDto,
  DomainConfigDto,
  UpdateDomainStatusRequestDto,
  UpdateDomainStatusResponseDto,
  UpdateOurStoreDomainDto,
  DomainToUpdateDto,
  DomainSuggestionsResponseDto,
  DomainOrderResponseDto,
  DomainPurchaseInitRequestDto,
  ProvinceDto,
  DomainUpdateContactRequestDto,
  DomainEmailResendResponseDto,
  DomainLifecycleDetailsResponseDto,
} from '@tiendanube/common';
import { cirrusApi } from 'App/rtk';
import getUrlWithParams from 'commons/utils/getUrlWithParams';
import {
  domainsEndpoints,
  domainsTags,
  BASE_DOMAINS_URL,
  DOMAIN_CONFIG_URL,
  DELETE_DOMAIN_URL,
  SET_PRIMARY_DOMAIN_URL,
  UPDATE_OUR_DOMAIN_URL,
  UPDATE_DOMAIN_STATUS_URL,
  GET_DOMAIN_TO_UPDATE_URL,
  SET_DOMAIN_TO_NOTIFY_URL,
  GET_HOSTNAME_DNS_VALUES_URL,
  GET_DOMAINS_ERRORS_URL,
  GET_DOMAIN_SUGGESTIONS_URL,
  CREATE_DOMAIN_ORDER_URL,
  GET_ACTIVE_DOMAIN_ORDER_URL,
  GET_DOMAIN_ORDER_URL,
  UPDATE_DOMAIN_ORDER_CONTACT_URL,
  ACKNOWLEDGE_DOMAIN_ORDER_URL,
  GET_DOMAIN_LIFECYCLE_DETAILS_URL,
  RESEND_VERIFICATION_EMAIL_URL,
  GET_COUNTRIES_URL,
  GET_PROVINCES_URL,
} from './config';

export const domainsApi = cirrusApi
  .enhanceEndpoints({
    addTagTypes: [domainsTags.domainsList, domainsTags.activeOrder],
  })
  .injectEndpoints({
    endpoints: (builder) => ({
      [domainsEndpoints.getDomains]: builder.query<StoreDomainDto[], void>({
        query: () => ({
          method: 'GET',
          url: BASE_DOMAINS_URL,
        }),
        providesTags: [domainsTags.domainsList],
      }),
      [domainsEndpoints.createDomain]: builder.mutation<
        StoreDomainDto[],
        CreateStoreDomainDto & { skipInvalidation?: boolean }
      >({
        query: ({ skipInvalidation: _skipInvalidation, ...newDomain }) => ({
          method: 'POST',
          url: BASE_DOMAINS_URL,
          data: newDomain,
        }),
        invalidatesTags: (_, error, arg) =>
          error || arg.skipInvalidation ? [] : [domainsTags.domainsList],
      }),
      [domainsEndpoints.deleteDomain]: builder.mutation<void, string>({
        query: (id) => ({
          method: 'DELETE',
          url: getUrlWithParams(DELETE_DOMAIN_URL, { id }),
        }),
        invalidatesTags: (_, error) => (error ? [] : [domainsTags.domainsList]),
      }),
      [domainsEndpoints.setPrimaryDomain]: builder.mutation<
        StoreDomainDto,
        string
      >({
        query: (id) => ({
          method: 'POST',
          url: getUrlWithParams(SET_PRIMARY_DOMAIN_URL, { id }),
        }),
        invalidatesTags: (_, error) => (error ? [] : [domainsTags.domainsList]),
      }),
      [domainsEndpoints.updateOurDomain]: builder.mutation<
        { success: boolean; new_url: string; redirectUrl: string },
        UpdateOurStoreDomainDto
      >({
        query: (updatedDomain) => ({
          method: 'PUT',
          url: UPDATE_OUR_DOMAIN_URL,
          data: updatedDomain,
        }),
        invalidatesTags: (_, error) => (error ? [] : [domainsTags.domainsList]),
      }),
      [domainsEndpoints.getDomainConfig]: builder.query<
        DomainConfigDto,
        string
      >({
        query: (id) => ({
          method: 'GET',
          url: getUrlWithParams(DOMAIN_CONFIG_URL, { id }),
        }),
      }),
      [domainsEndpoints.updateDomainStatus]: builder.mutation<
        UpdateDomainStatusResponseDto[],
        UpdateDomainStatusRequestDto
      >({
        query: (payload) => ({
          method: 'PATCH',
          url: UPDATE_DOMAIN_STATUS_URL,
          data: payload,
        }),
        invalidatesTags: (_, error) => (error ? [domainsTags.domainsList] : []),
        async onQueryStarted(_, { dispatch, queryFulfilled }) {
          const { data } = await queryFulfilled;
          dispatch(
            cirrusApi.util.updateQueryData(
              domainsEndpoints.getDomains as never,
              undefined as never,
              (domains: StoreDomainDto[]) =>
                domains.map((domain) => {
                  const updatedStatus = data.find(
                    (updated) => updated.id === domain.id,
                  );
                  if (updatedStatus)
                    return {
                      ...domain,
                      status: {
                        domain: updatedStatus.domain,
                        ssl: updatedStatus.ssl,
                      },
                    };
                  return domain;
                }),
            ),
          );
        },
      }),
      [domainsEndpoints.getDomainSoftBlock]: builder.query<
        DomainToUpdateDto | null,
        void
      >({
        query: () => ({
          method: 'GET',
          url: GET_DOMAIN_TO_UPDATE_URL,
        }),
        providesTags: [domainsTags.domainsList],
      }),
      [domainsEndpoints.setDomainToNotify]: builder.mutation<void, void>({
        query: () => ({
          method: 'POST',
          url: SET_DOMAIN_TO_NOTIFY_URL,
        }),
      }),
      [domainsEndpoints.getHostnameDnsValues]: builder.query<
        { a: string[]; cname: string; unexpectedCname: string },
        { hostname: string }
      >({
        query: (params) => ({
          method: 'GET',
          url: GET_HOSTNAME_DNS_VALUES_URL,
          params,
        }),
      }),
      [domainsEndpoints.getDomainsErrors]: builder.query<
        { ids: string[] },
        void
      >({
        query: () => ({
          method: 'GET',
          url: GET_DOMAINS_ERRORS_URL,
        }),
      }),
      [domainsEndpoints.getDomainSuggestions]: builder.query<
        DomainSuggestionsResponseDto,
        { query: string }
      >({
        query: (params) => ({
          method: 'GET',
          url: GET_DOMAIN_SUGGESTIONS_URL,
          params,
        }),
      }),
      [domainsEndpoints.createDomainOrder]: builder.mutation<
        DomainOrderResponseDto,
        DomainPurchaseInitRequestDto
      >({
        query: (data) => ({
          method: 'POST',
          url: CREATE_DOMAIN_ORDER_URL,
          data,
        }),
      }),
      [domainsEndpoints.getActiveDomainOrder]: builder.query<
        DomainOrderResponseDto | null,
        void
      >({
        query: () => ({
          method: 'GET',
          url: GET_ACTIVE_DOMAIN_ORDER_URL,
        }),
        providesTags: [domainsTags.activeOrder],
      }),
      [domainsEndpoints.getDomainOrder]: builder.query<
        DomainOrderResponseDto,
        string
      >({
        query: (payOrderId) => ({
          method: 'GET',
          url: getUrlWithParams(GET_DOMAIN_ORDER_URL, { payOrderId }),
        }),
      }),
      [domainsEndpoints.updateDomainOrderContact]: builder.mutation<
        DomainOrderResponseDto,
        { payOrderId: string; contact: DomainUpdateContactRequestDto }
      >({
        query: ({ payOrderId, contact }) => ({
          method: 'PUT',
          url: getUrlWithParams(UPDATE_DOMAIN_ORDER_CONTACT_URL, {
            payOrderId,
          }),
          data: contact,
        }),
      }),
      [domainsEndpoints.getCountries]: builder.query<
        | Array<{ id: string; countryName: string; countryCode: string }>
        | Record<string, string>,
        void
      >({
        query: () => ({
          method: 'GET',
          url: GET_COUNTRIES_URL,
        }),
      }),
      [domainsEndpoints.getProvinces]: builder.query<ProvinceDto[], string>({
        query: (countryCode) => ({
          method: 'GET',
          url: getUrlWithParams(GET_PROVINCES_URL, { countryCode }),
        }),
      }),
      [domainsEndpoints.resendVerificationEmail]: builder.mutation<
        DomainEmailResendResponseDto,
        string
      >({
        query: (storeId) => ({
          method: 'POST',
          url: getUrlWithParams(RESEND_VERIFICATION_EMAIL_URL, { storeId }),
        }),
      }),
      [domainsEndpoints.getDomainLifecycleDetails]: builder.query<
        DomainLifecycleDetailsResponseDto,
        string
      >({
        query: (domain) => ({
          method: 'GET',
          url: getUrlWithParams(GET_DOMAIN_LIFECYCLE_DETAILS_URL, { domain }),
        }),
      }),
      [domainsEndpoints.acknowledgeDomainOrder]: builder.mutation<
        DomainOrderResponseDto,
        string
      >({
        query: (payOrderId) => ({
          method: 'PUT',
          url: getUrlWithParams(ACKNOWLEDGE_DOMAIN_ORDER_URL, { payOrderId }),
        }),
        async onQueryStarted(payOrderId, { dispatch, queryFulfilled }) {
          let patchResult: { undo: () => void } | undefined;
          try {
            patchResult = dispatch(
              cirrusApi.util.updateQueryData(
                domainsEndpoints.getActiveDomainOrder as never,
                undefined as never,
                () => null as never,
              ),
            );
          } catch {
            patchResult = undefined;
          }

          try {
            await queryFulfilled;
            dispatch(
              cirrusApi.util.invalidateTags([domainsTags.activeOrder as never]),
            );
          } catch {
            patchResult?.undo();
          }
        },
      }),
    }),
  });

export const {
  useGetDomainsQuery,
  useCreateDomainMutation,
  useDeleteDomainMutation,
  useSetPrimaryDomainMutation,
  useUpdateOurDomainMutation,
  useGetDomainConfigQuery,
  useUpdateDomainStatusMutation,
  useGetDomainSoftBlockQuery,
  useLazyGetDomainSoftBlockQuery,
  useSetDomainToNotifyMutation,
  useGetHostnameDnsValuesQuery,
  useGetDomainsErrorsQuery,
  useGetDomainSuggestionsQuery,
  useLazyGetDomainSuggestionsQuery,
  useCreateDomainOrderMutation,
  useGetActiveDomainOrderQuery,
  useLazyGetActiveDomainOrderQuery,
  useGetDomainOrderQuery,
  useLazyGetDomainOrderQuery,
  useUpdateDomainOrderContactMutation,
  useAcknowledgeDomainOrderMutation,
  useGetDomainLifecycleDetailsQuery,
  useResendVerificationEmailMutation,
  useGetCountriesQuery,
  useGetProvincesQuery,
} = domainsApi;
